<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />

    <div :class="layoutClasses">
      <div
        v-if="label || description"
        class="formester-label-description-wrapper"
        :style="labelDescriptionWrapperStyles"
      >
        <label
          :class="labelClasses"
          :style="labelStyles"
          :for="item.label + item.id"
          v-html="label"
        />
        <SmartText
          :active="active"
          :editing="editing"
          :item="item"
          :style="descriptionStyles"
          :text="description"
        />
      </div>

      <div class="label-n-number">
        <div class="numbers-wrapper">
          <div
            v-for="num in range(item.start, item.end)"
            :key="num"
            class="rating-number-block"
          >
            <div
              class="rating-number"
              :style="inputStyles"
              :class="{
                active: num == item.value,
                selected: styling.multiPage && num === selected,
              }"
              tabindex="0"
              @keypress.space="setSelectedScale(num)"
              @click="handleScaleClick(num)"
            >
              {{ num }}
            </div>
            <div
              v-if="num == item.start"
              :class="subLabelClasses"
              :style="ratingSubLabelStyles"
            >
              {{ item.startlabel }}
            </div>
            <div
              v-if="num == item.end"
              class="highest-rating-label"
              :class="subLabelClasses"
              :style="ratingSubLabelStyles"
            >
              {{ item.endlabel }}
            </div>
          </div>
        </div>
      </div>
      <ErrorMessage :item="item" />
      <input
        :id="item.label"
        class="scale-rating-input"
        :value="item.value"
        :required="item.required"
        :name="`${item.label}`"
        :disabled="isDisabled"
      />
    </div>
  </div>
</template>

<script>
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import { set } from "lodash-es";
import { addAlpha } from "@/helpers/utils.js";

export default {
  name: "ScaleRating",
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      selected: null,
    };
  },
  computed: {
    inputStyles() {
      return {
        fontFamily: this.styling.fontFamily || "sans-serif",
        color: this.styling.answerTextColor || "#575757",
        cursor: this.isDisabled && !this.editing ? "not-allowed" : "pointer",
        opacity: this.isDisabled && !this.editing ? 0.6 : 1,
      };
    },
    scaleRatingLabelClasses() {
      return {
        ...this.subLabelClasses,
        "scale-sub_label": !this.editing,
      };
    },
    labelWrapper() {
      const end = +this.item.end;
      // for more than 3 blocks max width is calculated and set equal to numbers block width
      const maxWidth = end > 3 ? `${end * 40 + (end - 1) * 16}px` : "unset";
      return {
        maxWidth,
      };
    },
    ratingSubLabelStyles() {
      return {
        ...this.subLabelStyles,
        "margin-bottom": 0,
        "margin-top": "4px",
      };
    },
  },
  unmounted() {
    this.resetSelected();
  },
  methods: {
    range(start, end) {
      start = parseInt(start);
      end = parseInt(end);
      let res = [];
      for (let i = start; i <= end; i++) {
        res.push(i);
      }
      return res;
    },
    setSelectedScale(num) {
      set(this.item, "value", num);
    },
    handleScaleClick(num) {
      if (this.editing) return;
      if (this.isDisabled) return;

      this.selected = num;
      this.setSelectedScale(num);
      this.autoProgressToNext();
    },
    resetSelected() {
      this.selected = null;
    },
  },
};
</script>

<style scoped>
.numbers-wrapper {
  display: grid;
  text-align: center;
  grid-template-columns: repeat(auto-fill, 44px);
  gap: 8px 16px;
}

.label-n-number {
  width: 100%;
}

.rating-number {
  width: 44px;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: var(--placeholder-color);
  cursor: pointer;
  box-sizing: border-box;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.rating-number-block {
  display: flex;
  width: 44px;
  flex-direction: column;
  gap: 8px;
}

.highest-rating-label {
  align-self: end;
}

.rating-number.active,
.rating-number.active:hover,
.rating-number.active:focus-visible {
  background-color: var(--primary-color-five-opacity);
  border: 1px solid var(--primary-color);
  color: var(--primary-color) !important;
  opacity: 1;
}

.rating-number:hover,
.rating-number:focus-visible {
  background-color: var(--placeholder-color-fifty-opacity);
}

.rating-number.selected {
  animation: blink 200ms ease 40ms 3;
}

.star-rating-input {
  position: absolute;
  top: 0;
  opacity: 0;
}

.scale-rating-input {
  position: absolute;
  opacity: 0;
  top: 0;
}

@keyframes blink {
  from {
    background-color: transparent;
    border-color: var(--primary-color);
  }
  to {
    background-color: var(--primary-color-five-opacity);
    border-color: var(--primary-color);
  }
}

@media screen and (max-width: 680px) {
  .numbers-wrapper {
    grid-template-columns: repeat(5, 44px);
  }
}
</style>
